import { NvButton } from '@simwood/nv-react-components';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getSha256Hash, handleEnterPress } from '../../shared/helpers/util';
import CallControls from '../components/CallControls';
import Dialler from '../components/Dialler';
import {
  getAudioDevices,
  testCanChangeAudioDevice,
} from '../helpers/getAudioDevices';
import { doAction } from '../helpers/partnerCrm';
import { answerCall, endCall } from '../reducers/calls';
import { StoreDispatch, StoreState } from '../store';
import * as styles from '../styles/activeCall.module.scss';
import Monogram from './Monogram';
import Tag from './Tag';
import TimeElapsed from './TimeElapsed';
import { HangUpIcon, PhoneIcon } from './icons';

const ActiveCall: React.FC = () => {
  const dispatch = useDispatch<StoreDispatch>();
  const selectedAudioInputDeviceId = useSelector(
    (state: StoreState) => state.audio.selectedAudioInputDeviceId,
  );
  const selectedAudioOutputDeviceId = useSelector(
    (state: StoreState) => state.audio.selectedAudioOutputDeviceId,
  );
  const selectedRingingOutputDeviceId = useSelector(
    (state: StoreState) => state.audio.selectedRingingOutputDeviceId,
  );
  const activeCall = useSelector((state: StoreState) =>
    state.calls.find((c) => c.active === true),
  );
  const ringingCall = useSelector((state: StoreState) =>
    state.calls.find((c) => c.answering || (c.ringing && c.direction === 'IN')),
  );
  const call = activeCall || ringingCall;
  const fullCrmDetails = call?.crmDetails;
  // pick the first one, doesn't really matter?
  const crmDetails = fullCrmDetails?.[0]?.records.find((r) => !!r.displayText);

  const [selectedAudioInputDevice, setSelectedAudioInputDevice] =
    useState<any>(null);
  const [selectedAudioOutputDevice, setSelectedAudioOutputDevice] =
    useState<any>(null);
  const [canChangeAudioDevice, setCanChangeAudioDevice] = useState(false);
  const [isOpeningContact, setIsOpeningContact] = useState(false);

  const {
    answered,
    answering,
    ringing,
    name,
    number,
    direction,
    email,
    tags,
    started,
    description,
    id,
    diallerVisible,
    transferInitiated,
  } = call || {};

  const onAnswerClick = useCallback(() => {
    if (id) {
      dispatch(answerCall(id));
    }
  }, [dispatch, id]);

  const onEndClick = useCallback(() => {
    if (id) {
      dispatch(endCall(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    const canChangeAudioDevice = testCanChangeAudioDevice();

    getAudioDevices({
      selectedAudioInputDeviceId,
      selectedAudioOutputDeviceId,
      selectedRingingOutputDeviceId,
    })
      .then((devices) => {
        const { selectedAudioInputDevice, selectedAudioOutputDevice } = devices;

        setSelectedAudioInputDevice(selectedAudioInputDevice);
        setSelectedAudioOutputDevice(selectedAudioOutputDevice);
        setCanChangeAudioDevice(canChangeAudioDevice);
      })
      .catch(() => {
        setSelectedAudioInputDevice(null);
        setSelectedAudioOutputDevice(null);
        setCanChangeAudioDevice(canChangeAudioDevice);
      });
  }, [
    selectedAudioInputDeviceId,
    selectedAudioOutputDeviceId,
    selectedRingingOutputDeviceId,
  ]);

  const [gravatarHash, setGravatarHash] = useState<string>('');

  useEffect(() => {
    if (email) {
      getSha256Hash(email.trim().toLowerCase()).then(setGravatarHash);
    }
  }, [email]);

  // TODO fix gravatar size

  const usingMonogram = !email;
  const backgroundImgSrc =
    !usingMonogram &&
    gravatarHash &&
    `https://www.gravatar.com/avatar/${gravatarHash}`;
  const backgroundImgStyles = {
    backgroundImage: backgroundImgSrc ? `url(${backgroundImgSrc})` : undefined,
  };

  const inboundCall = direction === 'IN';

  // Blind-transfer keypad is inbound-only; outbound legs arm without a keypad.
  const showTransferKeypad = transferInitiated && inboundCall;

  const open = diallerVisible || showTransferKeypad;

  let showSplitButtons = false;

  if (inboundCall && ringing && !answering) {
    showSplitButtons = true;
  }

  // const nameEl = <span className={styles.name}>{name || number}</span>;

  const nameEl = (
    <span>{(crmDetails && crmDetails.displayText) || name || number}</span>
  );
  const showContactDetailsEl =
    fullCrmDetails &&
    fullCrmDetails
      .filter(
        (fcrmd) =>
          !!fcrmd.records.find((r) => !!r.showRecordActionId)
            ?.showRecordActionId,
      )
      .map((fcrmd) => (
        <NvButton
          key={fcrmd.integrationName}
          disabled={isOpeningContact}
          onClick={() => {
            setIsOpeningContact(true);
            doAction(
              fcrmd.records.find((r) => !!r.showRecordActionId)
                ?.showRecordActionId,
            ).finally(() => {
              setIsOpeningContact(false);
            });
          }}
        >
          Open on {fcrmd.integrationName}
        </NvButton>
      ));

  const descriptionEl = (
    <span className={styles.description}>
      {description && (
        <span className={styles.description_text}>{description}</span>
      )}
      {(tags || []).map((t, i) => (
        <Tag key={i} label={t} />
      ))}
    </span>
  );
  const infoEl = started ? (
    <TimeElapsed start={started} autoUpdate />
  ) : (
    <span>{inboundCall ? 'Incoming' : 'Calling'}</span>
  );

  const hasTags = (tags || []).length > 0;

  return (
    <div className={styles.call_wrapper}>
      <div className={styles.call_container}>
        {usingMonogram ? (
          <div className={styles.background_image_user}>
            <Monogram />
          </div>
        ) : (
          <div
            className={styles.background_image_user}
            style={backgroundImgStyles}
          ></div>
        )}

        <div className={open ? styles.content_open : styles.content}>
          <div className={styles.info}>
            <div className={styles.image_text_container}>
              <div className={styles.image_container}>
                {usingMonogram ? (
                  <div className={styles.image}>
                    <Monogram />
                  </div>
                ) : (
                  <img
                    alt=''
                    className={styles.image}
                    src={`${backgroundImgSrc}?s=500`}
                  />
                )}
              </div>
              {hasTags ? (
                <div className={styles.details_container_tags}>
                  <div className={styles.first_row}>
                    {nameEl}
                    {infoEl}
                    {showContactDetailsEl}
                  </div>
                  <div className={styles.second_row}>{descriptionEl}</div>
                </div>
              ) : (
                <div className={styles.details_container}>
                  <div className={styles.text_container}>
                    {nameEl}
                    {showContactDetailsEl}
                    {descriptionEl}
                  </div>
                  <div className={styles.time_container}>{infoEl}</div>
                </div>
              )}
            </div>
            <div className={styles.dialler_container}>
              {showTransferKeypad ? <Dialler transfer /> : <Dialler inCall />}
            </div>
          </div>
        </div>
      </div>
      {answered ? (
        <div className={styles.controls_container}>
          <CallControls />
        </div>
      ) : (
        ''
      )}
      {showSplitButtons ? (
        <div className={styles.button_container}>
          {canChangeAudioDevice &&
          (!selectedAudioInputDevice || !selectedAudioOutputDevice) ? (
            <div
              className={styles.button_answer_disabled}
              title='No audio input/output device connected'
            >
              <PhoneIcon />
            </div>
          ) : (
            <div
              className={styles.button_answer}
              onClick={onAnswerClick}
              onKeyDown={handleEnterPress(onAnswerClick)}
              role='button'
              tabIndex={0}
            >
              <PhoneIcon />
            </div>
          )}
          <div
            className={styles.button_reject}
            onClick={onEndClick}
            onKeyDown={handleEnterPress(onEndClick)}
            role='button'
            tabIndex={0}
          >
            <HangUpIcon />
          </div>
        </div>
      ) : (
        <div className={styles.button_container}>
          <div
            className={styles.button_end}
            onClick={onEndClick}
            onKeyDown={handleEnterPress(onEndClick)}
            role='button'
            tabIndex={0}
          >
            <HangUpIcon />
          </div>
        </div>
      )}
    </div>
  );
};

export default ActiveCall;
